import { useState, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { 
  Menu, Upload, FileSpreadsheet, Image, FileText, 
  Loader2, Check, X, Users, Building2, MessageSquare, Package,
  Eye, ArrowRight, AlertTriangle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { createPortal } from "react-dom";

const SmartUploadMenu = ({ userId, onUploadComplete }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const fileInputRef = useRef(null);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const previewFiles = useCallback(async (files) => {
    setIsProcessing(true);
    setUploadResult(null);
    setPreviewData(null);
    setPendingFile(files);

    try {
      const formData = new FormData();
      files.forEach((file) => formData.append('files', file));
      formData.append('userId', userId);

      const response = await axios.post(`${API}/upload/preview`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const previews = response.data.previews || [];
      if (previews.length > 0 && !previews[0].error) {
        setPreviewData(previews[0]);
      } else {
        toast.error(previews[0]?.error || "Could not preview file");
        setPreviewData(null);
      }
    } catch (error) {
      console.error("Preview error:", error);
      toast.error(error.response?.data?.detail || "Failed to preview file");
    } finally {
      setIsProcessing(false);
    }
  }, [userId]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) previewFiles(files);
  }, [previewFiles]);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) previewFiles(files);
  };

  const confirmImport = async () => {
    if (!previewData || !pendingFile) return;
    setIsConfirming(true);

    try {
      const formData = new FormData();
      pendingFile.forEach((file) => formData.append('files', file));
      formData.append('userId', userId);

      const res = await axios.post(`${API}/upload/smart-import`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setUploadResult(res.data);
      toast.success(`Imported ${res.data.totalRecords} records`);
      setPreviewData(null);
      setPendingFile(null);
      if (onUploadComplete) onUploadComplete(res.data);
    } catch (error) {
      console.error("Import error:", error);
      toast.error(error.response?.data?.detail || "Import failed");
    } finally {
      setIsConfirming(false);
    }
  };

  const cancelPreview = () => {
    setPreviewData(null);
    setPendingFile(null);
  };

  const getRouteIcon = (routeType) => {
    switch (routeType) {
      case "accounts": return <Building2 className="h-4 w-4 text-ism-teal" />;
      case "prospects": return <Users className="h-4 w-4 text-ism-teal" />;
      case "conversations": return <MessageSquare className="h-4 w-4 text-ism-teal" />;
      case "products": return <Package className="h-4 w-4 text-ism-teal" />;
      default: return <FileText className="h-4 w-4 text-gray-400" />;
    }
  };

  const getRouteLabel = (routeType) => {
    switch (routeType) {
      case "accounts": return "Accounts";
      case "prospects": return "Prospects/Leads";
      case "conversations": return "Conversations";
      case "products": return "Products Owned";
      default: return "Other Data";
    }
  };

  const handleClose = () => {
    if (!isProcessing && !isConfirming) {
      setIsOpen(false);
      setUploadResult(null);
      setPreviewData(null);
      setPendingFile(null);
    }
  };

  const modalContent = isOpen ? (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div onClick={handleClose} style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0, 0, 0, 0.9)' }} />
      
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        style={{
          position: 'relative', width: '100%', maxWidth: previewData ? '600px' : '450px', margin: '0 16px',
          backgroundColor: '#111111', borderRadius: '16px', border: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)', maxHeight: '85vh', display: 'flex', flexDirection: 'column'
        }}
        data-testid="smart-upload-modal"
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px', borderBottom: '1px solid rgba(255, 255, 255, 0.1)', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ padding: '10px', borderRadius: '10px', backgroundColor: 'rgba(0, 163, 138, 0.2)' }}>
              <Upload className="h-5 w-5 text-ism-teal" />
            </div>
            <div>
              <h2 style={{ fontSize: '18px', fontWeight: '600', color: 'white', margin: 0 }}>
                {previewData ? "Preview Import" : "Smart Import"}
              </h2>
              <p style={{ fontSize: '13px', color: '#888', margin: 0 }}>
                {previewData ? `${previewData.totalRows} rows detected` : "AI routes data to the right place"}
              </p>
            </div>
          </div>
          <button onClick={handleClose} disabled={isProcessing || isConfirming}
            style={{ padding: '8px', borderRadius: '8px', backgroundColor: 'transparent', border: 'none', cursor: 'pointer' }}>
            <X className="h-5 w-5 text-gray-400 hover:text-white" />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '24px', overflowY: 'auto', flex: 1 }}>
          {/* Preview Mode */}
          {previewData ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* Classification */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px', backgroundColor: 'rgba(0, 163, 138, 0.1)', borderRadius: '10px', border: '1px solid rgba(0, 163, 138, 0.2)' }}>
                {getRouteIcon(previewData.type)}
                <div>
                  <p style={{ fontSize: '14px', color: 'white', margin: 0 }}>
                    Classified as <strong>{getRouteLabel(previewData.type)}</strong>
                  </p>
                  <p style={{ fontSize: '12px', color: '#888', margin: 0 }}>
                    {previewData.totalRows} rows • {previewData.confidence} confidence
                  </p>
                </div>
              </div>

              {/* Column Mapping */}
              <div>
                <p style={{ fontSize: '13px', color: '#888', margin: '0 0 8px' }}>Column Mapping</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  {Object.entries(previewData.columnMap || {}).map(([src, tgt]) => (
                    <div key={src} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 10px', backgroundColor: 'rgba(255,255,255,0.03)', borderRadius: '6px', fontSize: '13px' }}>
                      <span style={{ color: '#888', minWidth: '120px' }}>{src}</span>
                      <ArrowRight className="h-3 w-3 text-ism-teal" />
                      <span style={{ color: '#ccc' }}>{tgt}</span>
                    </div>
                  ))}
                  {previewData.sourceColumns?.filter(c => !previewData.columnMap?.[c]).map(col => (
                    <div key={col} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 10px', backgroundColor: 'rgba(255,255,255,0.02)', borderRadius: '6px', fontSize: '13px' }}>
                      <span style={{ color: '#555', minWidth: '120px' }}>{col}</span>
                      <span style={{ color: '#444', fontSize: '11px' }}>skipped</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Preview Table */}
              <div>
                <p style={{ fontSize: '13px', color: '#888', margin: '0 0 8px' }}>Preview (first {previewData.previewRows?.length || 0} rows)</p>
                <div style={{ overflowX: 'auto', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                    <thead>
                      <tr>
                        {Object.values(previewData.columnMap || {}).map(col => (
                          <th key={col} style={{ padding: '8px 10px', textAlign: 'left', color: '#888', borderBottom: '1px solid rgba(255,255,255,0.1)', backgroundColor: 'rgba(255,255,255,0.03)' }}>
                            {col}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {(previewData.previewRows || []).map((row, idx) => (
                        <tr key={`${idx}:${Object.values(row)[0] ?? ""}`}>
                          {Object.values(previewData.columnMap || {}).map(col => (
                            <td key={col} style={{ padding: '6px 10px', color: '#ccc', borderBottom: '1px solid rgba(255,255,255,0.05)', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {row[col] || "—"}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                <Button onClick={cancelPreview} variant="outline" className="flex-1 border-white/20 text-gray-300 hover:bg-white/5">
                  Cancel
                </Button>
                <Button onClick={confirmImport} disabled={isConfirming} className="flex-1 bg-ism-teal hover:bg-ism-teal/90 text-black font-medium" data-testid="confirm-import-btn">
                  {isConfirming ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Importing...</> : <><Check className="h-4 w-4 mr-2" /> Confirm Import ({previewData.totalRows} rows)</>}
                </Button>
              </div>
            </div>
          ) : !uploadResult ? (
            /* Drop Zone */
            <div
              onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}
              onClick={() => !isProcessing && fileInputRef.current?.click()}
              style={{
                border: `2px dashed ${isDragging ? '#00A38A' : 'rgba(255, 255, 255, 0.2)'}`,
                borderRadius: '12px', padding: '40px 24px', textAlign: 'center',
                cursor: isProcessing ? 'not-allowed' : 'pointer',
                backgroundColor: isDragging ? 'rgba(0, 163, 138, 0.1)' : 'rgba(255, 255, 255, 0.02)',
                transition: 'all 0.2s ease', opacity: isProcessing ? 0.5 : 1
              }}
              data-testid="upload-drop-zone"
            >
              <input ref={fileInputRef} type="file" multiple accept=".csv,.xlsx,.xls,.png,.jpg,.jpeg,.gif,.webp,.txt,.pdf" onChange={handleFileSelect} style={{ display: 'none' }} />
              {isProcessing ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                  <Loader2 className="h-10 w-10 text-ism-teal animate-spin" />
                  <p style={{ fontSize: '14px', color: '#888', margin: 0 }}>Analyzing with AI...</p>
                  <p style={{ fontSize: '12px', color: '#666', margin: 0 }}>Mapping columns and classifying data</p>
                </div>
              ) : (
                <>
                  <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', marginBottom: '16px' }}>
                    <div style={{ padding: '12px', borderRadius: '10px', backgroundColor: 'rgba(0, 163, 138, 0.15)' }}>
                      <FileSpreadsheet className="h-6 w-6 text-ism-teal" />
                    </div>
                    <div style={{ padding: '12px', borderRadius: '10px', backgroundColor: 'rgba(0, 163, 138, 0.15)' }}>
                      <Image className="h-6 w-6 text-ism-teal" />
                    </div>
                    <div style={{ padding: '12px', borderRadius: '10px', backgroundColor: 'rgba(0, 163, 138, 0.15)' }}>
                      <FileText className="h-6 w-6 text-ism-teal" />
                    </div>
                  </div>
                  <p style={{ fontSize: '16px', color: 'white', margin: '0 0 8px 0' }}>
                    {isDragging ? "Drop files here" : "Drag & drop files here"}
                  </p>
                  <p style={{ fontSize: '14px', color: '#888', margin: '0 0 16px 0' }}>or click to browse</p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '8px' }}>
                    {['CSV', 'Excel', 'PDF', 'Images', 'Text'].map(type => (
                      <span key={type} style={{ padding: '4px 10px', fontSize: '12px', backgroundColor: 'rgba(255, 255, 255, 0.05)', borderRadius: '20px', color: '#888' }}>{type}</span>
                    ))}
                  </div>
                </>
              )}
            </div>
          ) : (
            /* Upload Result */
            <div>
              {uploadResult.error ? (
                <div style={{ padding: '16px', borderRadius: '12px', backgroundColor: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.2)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#f87171' }}>
                    <X className="h-5 w-5" />
                    <span style={{ fontSize: '14px', fontWeight: '500' }}>{uploadResult.message}</span>
                  </div>
                </div>
              ) : (
                <div style={{ padding: '16px', borderRadius: '12px', backgroundColor: 'rgba(0, 163, 138, 0.1)', border: '1px solid rgba(0, 163, 138, 0.2)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#00A38A', marginBottom: '12px' }}>
                    <Check className="h-5 w-5" />
                    <span style={{ fontSize: '14px', fontWeight: '600' }}>Import Complete</span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
                    {uploadResult.routes?.map((route, idx) => (
                      <div key={route.type || idx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', backgroundColor: 'rgba(255, 255, 255, 0.05)', borderRadius: '8px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {getRouteIcon(route.type)}
                          <span style={{ fontSize: '14px', color: '#ccc' }}>{getRouteLabel(route.type)}</span>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <span style={{ fontSize: '14px', color: '#00A38A', fontWeight: '500' }}>{route.count} imported</span>
                          {route.skippedBlank > 0 && (
                            <span style={{ fontSize: '11px', color: '#888', display: 'block' }}>{route.skippedBlank} blank rows skipped</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  <Button onClick={handleClose} className="w-full bg-ism-teal hover:bg-ism-teal/90 text-white">Done</Button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {!previewData && !uploadResult && (
          <div style={{ padding: '16px 24px', borderTop: '1px solid rgba(255, 255, 255, 0.05)', flexShrink: 0 }}>
            <p style={{ fontSize: '12px', color: '#666', textAlign: 'center', margin: 0 }}>
              AI maps columns, previews data, then you confirm before import
            </p>
          </div>
        )}
      </motion.div>
    </div>
  ) : null;

  return (
    <>
      <Button onClick={() => setIsOpen(true)} variant="ghost" size="sm"
        className="p-2 rounded-lg bg-white/10 border border-white/20 hover:bg-white/20 hover:border-ism-teal/50 transition-all"
        data-testid="smart-upload-menu-trigger">
        <Menu className="h-5 w-5 text-white" />
      </Button>
      {typeof document !== 'undefined' && createPortal(
        <AnimatePresence>{modalContent}</AnimatePresence>,
        document.body
      )}
    </>
  );
};

export default SmartUploadMenu;
